import type { MediaPlayerInstance } from "@vidstack/react";
import type {
	ColorEnum,
	GPEventResponse,
	GameClipsResponse,
	GameClipIntervalRequest,
	GameClipResponse,
	GameEventFilterResponse,
	RatingVisibility,
	RatingIcon,
	GamePageLazyResponse,
	UserWidgetSettingsResponse,
	saveUserWidgetSettingsRequestBody,
	GameAnalysisPhysicalResponse,
	GPGamePlayerResponse as GPGamePlayerResponseApi,
} from "../../../portal/src/libs/apiClient";
import type {
	ApiResponse,
	CoachReviewsSummaryResponse,
	CoachReviewsResponse,
	PlayerCoachReviewPostRequest,
	TeamCoachReviewPostRequest,
} from "../../../discover/app/libs/api-client";
import type { RefObject } from "react";

export type {
	GamePageResponse,
	GPEventResponse,
	GPPlayerGradingResponse,
	GPPlayerPositionResponse,
	GPGameResponse,
	GPGameTeamResponse,
	GPPermissionsResponse,
	GPVideoResponse,
	GPPSPlayerResponse,
	GPTSItemResponse,
	GameTeamStatTypeEnum,
	GameTeamStatCategoryEnum,
	GamePlayerStatColumnEnum,
	GamePlayerStatResponse,
	GameClipIntervalRequest,
	GameClipEventRequest,
	GameClipsRowResponse,
	GamePlayerItemRequest,
	GameTeamPutColorsRequest,
	GameTeamSheetCompositionPlayersResponse,
	GTSCompositionPosition,
	GameTeamSheetGameTeamPlayersSearchResponse,
	GSTGameTeamPlayerResponse,
	GameTeamSheetPlayersResponse,
	GameClipResponse,
	GPHalfResponse,
	GameStatsExportRequest,
	GameStatsExportCellRequest,
	GPDownloadTypeResponse,
	GameEventDataExportRequest,
	GameEventFilterResponse,
	GameEventFilterItemResponse,
	GameEventFilterPostRequest,
	GameEventFilterItemPostRequest,
	RatingPutRequest,
	BadgesPutRequest,
	RatingBadgeResponse,
	RatingVisibility,
	RatingIcon,
	GamePageLazyResponse,
	UserWidgetSettingsResponse,
	saveUserWidgetSettingsRequestBody,
} from "../../../portal/src/libs/apiClient";

export type GPGamePlayerResponse = GPGamePlayerResponseApi & {
	gradedByScouts?: number | null;
};

export interface User {
	[x: string]: unknown;
}

export enum UserRole {
	Recruiter = "ROLE_RECRUITER",
	Admin = "ROLE_ADMIN",
	ClubManager = "ROLE_CLUB_MANAGER",
	Coach = "ROLE_COACH",
}

export interface Colors {
	shirt?: ColorEnum;
	shorts?: ColorEnum;
}

export interface SelectColors {
	homeTeam: Colors;
	awayTeam: Colors;
}

export enum GamePageSortDirection {
	Asc = "asc",
	Desc = "desc",
}

export enum NavOptions {
	Admin = "Admin",
	TeamStats = "TeamStats",
	PlayerStats = "PlayerStats",
	Clips = "Clips",
	FullGame = "FullGame",
	DataInsights = "DataInsights",
	Offensive = "offensive",
	Defensive = "defensive",
	Distribution = "distribution",
	Goalkeeper = "goalkeeper",
	PhysicalAnalysis = "PhysicalAnalysis",
}

export enum PlayerRatingIcon {
	Good = "good",
	Neutral = "neutral",
	Bad = "bad",
}

export enum PlayerRatingVisibility {
	Coach = "coach",
	Player = "player",
	PlayerParent = "player_and_parent",
}

export interface Interval {
	start: number;
	end: number;
	length: number;
}

export type VideoPlayerRefContextValue = RefObject<MediaPlayerInstance> | null;

export interface VideoPlayerContextValue {
	playerRef: RefObject<MediaPlayerInstance> | null;
	canPlay: boolean;
	isPlaying: boolean;
	setIsPlaying: (isPlaying: boolean) => void;
}

export enum VideoSidebar {
	Timeline = "timeline",
	TeamSheet = "teamSheet",
	EventList = "eventList",
	Filters = "filters",
	CreateEvent = "createEvent",
	EditEvent = "editEvent",
	CreateClip = "createClip",
	EventsWithComments = "eventsWithComments",
	CoachingTools = "coachingTools",
	TeamPlayerReviews = "teamPlayerReviews",
	AttachMode = "attachMode",
}

export enum TeamFilterKey {
	Home = "team.home",
	Away = "team.away",
}

export enum AttachModeKind {
	Team = "team",
	Player = "player",
}

export type AttachModeTeamTarget = {
	kind: AttachModeKind.Team;
	teamId: number;
	name: string;
	filterKey: TeamFilterKey;
};

export type AttachModePlayerTarget = {
	kind: AttachModeKind.Player;
	playerId: number;
	name: string;
};

export type AttachModeTarget = AttachModeTeamTarget | AttachModePlayerTarget;

export interface Halves {
	firstHalfStart: number;
	firstHalfEnd: number;
	secondHalfStart: number;
	secondHalfEnd: number;
	extraHalfOneStart: number;
	extraHalfOneEnd: number;
	extraHalfTwoStart: number;
	extraHalfTwoEnd: number;
	extraHalfThreeStart: number;
	extraHalfThreeEnd: number;
}

export enum EventTypes {
	Goal = "goal",
	GoalOwn = "own_goal",
	Card = "card",
	Substitution = "substitution",
	Recovery = "recovery",
	Interception = "interception",
	PassReceived = "pass_received",
}

export enum CardTypes {
	Red = "red",
	Yellow = "yellow",
}

export enum SubstitutionTypes {
	On = "on",
	Off = "off",
}

export interface EventFilterView {
	key: string;
	type: string;
	value: string;
	label?: string;
	options?: Map<string, string>;
	nested?: EventFilterView[];
	prefilter?: EventViewPrefilter;
}

export interface EventViewPrefilter {
	type?: string[];
	period?: string[];
	details?: {
		[propName: string]: string[];
	};
}

export interface EventFilterValue {
	key: string;
	value: string[];
}

export enum FilterCategory {
	Team = "team",
	Player = "player",
	Period = "period",
	EventGroup = "eventGroup",
	Event = "event",
	EventFlag = "eventFlag",
	HighlightGroup = "highlightGroup",
	HighlightItem = "highlight",
	Divider = "divider",
	HasComments = "hasComments",
}

export interface EventFilterCondition {
	type: "id" | "type" | "period" | "success" | "under_pressure" | "flag" | "player" | "team" | string;
	flag?: string;
	value: string[];
}

export type MergedGpEventResponse = {
	id: number;
	categories: string[];
	types: string[];
	success: boolean | null;
	underPressure: boolean | null;
	gamePlayerId: number | null;
	gameTeamId: number | null;
	playerName: string | null;
	backNumber: number | null;
	x: number | null;
	y: number | null;
	half: number | null;
	halfTime: number | null | undefined;
	halfOvertime: number | null | undefined;
	footballTime: number | null;
	videoTime: number;
	lastVideoTime: number;
	startTime: number;
	endTime: number;
	providerEventId: string | null;
	relativeProviderEventId: string | null;
	details: Record<string, string | boolean | number> | null;
	prependSeconds: number;
	appendSeconds: number;
	hits: number[];
	isMutated: boolean;
	eventIds: number[];
	comments: any[];
	teamClubLogo: string | null;
	teamShirtColor: string | null;
};

export interface TeamPlayer {
	id: number;
	name: string;
	photo: string | null;
}

export interface CreateFormValues {
	name: string;
	note: string;
	start: string;
	end: string;
}

export const eventListConfig = {
	prepend: {
		max: 30,
		min: 2,
		default: 5,
	},
	append: {
		max: 30,
		min: 2,
		default: 10,
	},
};

export interface HighlightHandlers {
	create: (note: string, timestamp: number, title: string) => void;
	edit: (note: string, timestamp: number, title: string, id?: number) => void;
	delete: (id: number) => void;
}

export interface ClipsUtilityProps {
	create: (intervals: GameClipIntervalRequest[], name: string, description?: string) => void;
	edit: (id: number, name: string, description?: string) => void;
	delete: (id: number) => void;
	selectClip: (id: number) => void;
	selectedClip?: GameClipResponse;
	data?: GameClipsResponse;
	pagination: { page: number; limit: number };
	isLoading: boolean;
	isSelectedClipLoading: boolean;
	onPaginationChange: (pagination: { page: number; limit: number }) => void;
	generateShareLink: (
		id: number,
		description: string | undefined,
		password: string | undefined,
		expireDate: string | undefined,
		isPasswordUpdated: boolean
	) => void;
	newShareSlug: { slug: string; id: number };
}

export interface GameEventFilterUtilityProps {
	create: (name: string, filterValues: EventFilterValue[]) => void;
	delete: (id: number) => void;
	isLoading: boolean;
	data?: GameEventFilterResponse[];
}

export interface EventListTeamInfo {
	homeTeam: {
		id: number;
		shirtColor: string | null;
		clubLogo?: string | null;
		clubName?: string | null;
	};
	awayTeam: {
		id: number;
		shirtColor: string | null;
		clubLogo?: string | null;
		clubName?: string | null;
	};
}

export interface PlayerRatingModalData {
	id: number;
	show: boolean;
	name: string | null;
	rating: {
		score?: number | null;
		visibility?: RatingVisibility | null;
		ratedBy?: string | null;
		ratedAt?: string | null;
	} | null;
	players?: GPGamePlayerResponse[];
}

export enum PlayerBadgesModalMode {
	Save = "save",
	Attach = "attach",
}

export interface PlayerBadgesModalData {
	id: number;
	show: boolean;
	name: string | null;
	badgeIds: number[];
	mode: PlayerBadgesModalMode;
	players?: GPGamePlayerResponse[];
}

export enum PlayerGradingSubmitType {
	Post = "post",
	Put = "put",
	Delete = "delete",
}

export interface PlayerGradingSubmitPostPutPayload {
	type: PlayerGradingSubmitType.Post | PlayerGradingSubmitType.Put;
	id: number;
	performanceGrade: number;
	potentialGrade: number | null;
	note: string | null;
}

export interface PlayerGradingSubmitDeletePayload {
	type: PlayerGradingSubmitType.Delete;
	id: number;
}

export type PlayerGradingSubmitPayload = PlayerGradingSubmitPostPutPayload | PlayerGradingSubmitDeletePayload;

export interface EventComment {
	text: string;
	lockerRoomVisibility: "players" | "players_and_parents" | "tagged_players" | "tagged_players_and_their_parents";
	setChoicesAsDefault: boolean;
	visibleToAll: boolean;
}

export interface GameEventCommentProps {
	create: (id: number, data: EventComment) => void | Promise<void>;
	edit: (id: number, data: any) => void;
	delete: (id: number, eventId: number) => void;
	isLoading?: boolean;
}

export interface EventCommentItem {
	canEdit: boolean;
	canDelete: boolean;
	id: number;
	providerEventId: string;
	createdBy: string;
	createdAt: string;
	text: string;
	lockerRoomVisibility: "players" | "players_and_parents" | "tagged_players" | "tagged_players_and_their_parents";
	visibleToAll: boolean;
	mentions?: any;
}

export interface GameInsightsSettingsProps {
	saveSettings: (settings: saveUserWidgetSettingsRequestBody) => Promise<void>;
	gamePageIsLoading: boolean;
	events?: GPEventResponse[];
	userWidgetSettingsData?: UserWidgetSettingsResponse | null;
}

export interface SecondBallAnalysisResult {
	teamId: number;
	totalDuels: number;
	secondBallsWon: number;
	secondBallWinRate: number;
}

export enum TeamSide {
	Home = "home",
	Away = "away",
}

export enum ProcessingLevelEnum {
	Full = "full",
	Limited = "limited",
}

export enum FilterFlagsType {
	MultipleFlags = "multipleFlags",
	SingleFlags = "singleFlags",
}

export enum FilterFlagsKey {
	OffensiveShotInsidePa = "event.offensive.shot.inside_pa",
	DefensiveRecovery = "event.defensive.recovery",
	DistributionPassReceived = "event.distribution.pass_received",
}

export interface PhysicalAnalysis {
	isLoading: boolean;
	data?: GameAnalysisPhysicalResponse;
	error?: Error | null;
	handlePostPhysicalAnalysisReportUrls?: (urls: string[]) => Promise<void>;
}

export enum GenderEnum {
	Male = "Male",
	Female = "Female",
	Mixed = "Mixed",
}

export const GenderOptions: { label: string; value: GenderEnum }[] = [
	{ label: GenderEnum.Male, value: GenderEnum.Male },
	{ label: GenderEnum.Female, value: GenderEnum.Female },
];

export enum AICoachReviewsMode {
	All = "all",
	Player = "player",
	Team = "game",
}

export enum AICoachReviewsTone {
	Balanced = "balanced",
	Motivational = "motivational",
	Professional = "professional",
}

export enum AICoachReviewsLength {
	Short = "short",
	Medium = "medium",
	Detailed = "detailed",
}

interface GetCoachReviewsParams {
	id: number;
}

interface GetCoachReviewsSummaryParams {
	id: number;
}

interface SendPlayerReviewData {
	id: number;
	body: PlayerCoachReviewPostRequest;
}

interface SendTeamReviewData {
	id: number;
	body: TeamCoachReviewPostRequest;
}

export type CoachReviewPromises = {
	getCoachReviews: (params: GetCoachReviewsParams) => Promise<ApiResponse<CoachReviewsResponse>>;
	getCoachReviewsSummary: (params: GetCoachReviewsSummaryParams) => Promise<ApiResponse<CoachReviewsSummaryResponse>>;
	sendPlayerReview: (data: SendPlayerReviewData) => Promise<unknown>;
	sendTeamReview: (data: SendTeamReviewData) => Promise<unknown>;
	issueAccessToken: () => Promise<ApiResponse<{ token: string }>>;
};
